/**
 * Dev Tools Achievements Page
 * Development page for testing achievement components
 */

import React, { useState } from 'react'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../constants/colors'
import { AchievementBadge, AchievementRow } from '../../gamer/achievements/components'
import {
  Achievement,
  AchievementType,
  AchievementId,
  AchievementLevel
} from '../../gamer/achievements/types/achievementTypes'
import { getRoleFromPath } from '../../../config/routes/routeEnums'
import { useLocation } from 'react-router-dom'

/**
 * DevToolsAchievementsPage - Dev tools page for testing achievements
 */
export const DevToolsAchievementsPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const role = getRoleFromPath(location.pathname) || 'gamer'

  // Mock achievements for testing
  const [mockAchievements] = useState<Achievement[]>([
    {
      id: AchievementId.COMPLETED_FIRST_GAME,
      name: 'First Game Complete',
      description: 'You completed your first game!',
      isCompleted: true,
      type: AchievementType.SIMPLE
    },
    {
      id: AchievementId.COMPLETED_FIRST_GAME,
      name: 'First Game Complete',
      description: 'You completed your first game!',
      isCompleted: false,
      type: AchievementType.SIMPLE
    },
    {
      id: AchievementId.URANIUM_COLLECTED,
      name: 'Uranium Collector',
      description: 'Collect uranium',
      type: AchievementType.TIERED,
      currentValue: 10,
      thresholds: {
        Bronze: 25,
        Silver: 50,
        Gold: 100,
        Platinum: 100
      },
      nextLevel: AchievementLevel.BRONZE,
      progressPercent: 40
    },
    {
      id: AchievementId.URANIUM_COLLECTED,
      name: 'Uranium Collector',
      description: 'Collect uranium',
      type: AchievementType.TIERED,
      currentValue: 30,
      thresholds: {
        Bronze: 25,
        Silver: 50,
        Gold: 100,
        Platinum: 100
      },
      level: AchievementLevel.BRONZE,
      nextLevel: AchievementLevel.SILVER,
      progressPercent: 20
    },
    {
      id: AchievementId.URANIUM_COLLECTED,
      name: 'Uranium Collector',
      description: 'Collect uranium',
      type: AchievementType.TIERED,
      currentValue: 60,
      thresholds: {
        Bronze: 25,
        Silver: 50,
        Gold: 100,
        Platinum: 100
      },
      level: AchievementLevel.SILVER,
      nextLevel: AchievementLevel.GOLD,
      progressPercent: 20
    },
    {
      id: AchievementId.URANIUM_COLLECTED,
      name: 'Uranium Collector',
      description: 'Collect uranium',
      type: AchievementType.TIERED,
      currentValue: 100,
      thresholds: {
        Bronze: 25,
        Silver: 50,
        Gold: 100,
        Platinum: 100
      },
      level: AchievementLevel.GOLD,
      progressPercent: 100
    }
  ])

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <Box sx={{ padding: '24px', backgroundColor: INTERAX_COLORS.offBlack, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <Button variant="secondary" onClick={handleBack} icon={<ArrowLeftIcon className="w-4 h-4" />} />
        <Box className="text-headline-1" sx={{ color: INTERAX_COLORS.white }}>
          Achievements Dev Tools ({role})
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: '1400px' }}>
        <Box className="text-headline-2" sx={{ color: INTERAX_COLORS.white, marginBottom: '16px' }}>
          Achievement Components Testing
        </Box>
        <Box className="text-body-1" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '32px' }}>
          Test achievement components with different states and levels
        </Box>

        {/* Badge Examples */}
        <Box sx={{ marginBottom: '48px' }}>
          <Box className="text-headline-3" sx={{ color: INTERAX_COLORS.white, marginBottom: '24px' }}>
            Achievement Badges
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)'
              },
              gap: 3
            }}
          >
            {mockAchievements.map((achievement, index) => (
              <AchievementBadge key={`${achievement.id}-${index}`} achievement={achievement} size="medium" />
            ))}
          </Box>
        </Box>

        {/* Row Examples */}
        <Box>
          <Box className="text-headline-3" sx={{ color: INTERAX_COLORS.white, marginBottom: '24px' }}>
            Achievement Rows
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {mockAchievements.map((achievement, index) => (
              <AchievementRow key={`row-${achievement.id}-${index}`} achievement={achievement} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
